import { getFileExtensionType } from "../../utilFunctions";
import { fetchCKYCDocuments, fetchLandDocuments, fetchMultipleEndorsementDocument, fetchSingleCustomerPolicyDocument, fetchTenantFormDocument,fetchSinglePolicyDocument, fetchProposalFormDocument} from "../apiHelper";
// Helper function to create a document object
export const createDocument = (id, name, type, source, fileType, docURLContent) => {
  if (!docURLContent) return null;
  const cleanedContent = docURLContent.replace(/^file:/, '');
  let dataUrlPrefix;

  if (fileType === 'pdf') {
    dataUrlPrefix = 'data:application/pdf;base64,';
  } else if (['jpg', 'jpeg', 'png', 'gif'].includes(fileType)) {
    dataUrlPrefix = `data:image/${fileType};base64,`;
  } else if (["Video"].includes(fileType)) {
    dataUrlPrefix = `data:video/${fileType};base64,`;
  } else {
    console.warn(`Unknown file type: ${fileType}. Defaulting to image/jpeg.`);
    dataUrlPrefix = 'data:image/jpeg;base64,';
  }
  return {
    id,
    name,
    type,
    source,
    fileType,
    docURL: `${dataUrlPrefix}${cleanedContent}`
  };
};

// --- Document Fetching Functions ---
export const fetchPolicyDocument = async (policyDocId, policyDocName) => {
    try {
        const response = await fetchSinglePolicyDocument(policyDocId);
        return createDocument(
            policyDocId,
            `Policy Document for ${policyDocName}`,
            'Policy',
            'internal',
            getFileExtensionType(policyDocName),
            response?.Response?.imageUrl
        );
    } catch (error) {
        console.error("Failed to fetch internal policy document:", error);
        return null;
    }
};

export const fetchCustomerDocument = async (policyDocId, policyDocName) => {
    try {
        const response = await fetchSingleCustomerPolicyDocument(policyDocId);
        return createDocument(
            policyDocId,
            `Customer Information Sheet for ${policyDocName}`,
            'Customer Information Sheet',
            'customer',
            getFileExtensionType(policyDocName),
            response?.data?.response
        );
    } catch (error) {
        console.error("Failed to fetch customer policy document:", error);
        return null;
    }
};

export const fetchProposalDocument = async (transactionId, policyDocName) => {
  try {
    const { formDocument, videoDocument } = await fetchProposalFormDocument(transactionId);

    const formDoc = createDocument(
      transactionId,
      `Proposal Form for ${policyDocName}`,
      'Proposal',
      'proposal',
      getFileExtensionType(policyDocName),
      formDocument?.data?.imageUrl
    );

    const videoDoc = createDocument(
      transactionId,
      `Proposal Video for ${policyDocName.replace("pdf","mp4")}`,
      'Proposal',
      'proposal_video',
      getFileExtensionType(policyDocName.replace("pdf","mp4")),
      videoDocument?.data?.imageUrl
    );
    return [formDoc, videoDoc];
  } catch (error) {
    console.error("Failed to fetch proposal documents:", error);
    return null;
  }
};

export const fetchEndorsementDocuments = async (endorsementNumber) => {
    const endorsementDocs = [];
    if (endorsementNumber) {
        const [endorsementBase, countStr] = endorsementNumber.split('_');
        const count = Number(countStr);

        for (let i = 1; i <= count; i++) {
            const paddedIndex = String(i).padStart(3, '0');
            try {
                const res = await fetchMultipleEndorsementDocument(endorsementBase, paddedIndex);
                const doc = createDocument(
                    endorsementNumber,
                    `Endorsement Document ${paddedIndex}`,
                    `Endorsement`,
                    'endorsement',
                    'pdf',
                    res?.data?.imageUrl
                );
                if (doc) endorsementDocs.push(doc);
            } catch (err) {
                console.error(`Failed to fetch endorsement document ${i}:`, err);
            }
        }
    }
    return endorsementDocs;
};

export const fetchKYCDocuments = async (policyDetailCookie) => {
    const kycDocs = [];
    try {
        const farmerId = policyDetailCookie?.farmer_id;
        const ckycResponse = await fetchCKYCDocuments({ farmer_id: farmerId });
        const { frontImage, backImage, photo } = ckycResponse?.data?.Response || {};
        const images = [
            { key: 'frontImage', label: 'Front Image', value: frontImage },
            { key: 'backImage', label: 'Back Image', value: backImage },
            { key: 'photo', label: 'Customer Photo', value: photo }
        ];

        images.forEach(({ label, value }, index) => {
            if (value) {
                const cleanedContent = value.replace(/^file:/, '');
                const fileType = 'png';

                const doc = createDocument(
                    `kyc_doc_${index}`,
                    label,
                    'KYC',
                    'kyc',
                    fileType,
                    cleanedContent
                );

                if (doc) kycDocs.push(doc);
            }
        });
    } catch (error) {
        console.error("Failed to fetch CKYC documents:", error);
    }
    return kycDocs;
};

export const fetchLandDocumentsData = async (polygonId, records) => {
    const landDocs = [];
    try {
        const landResponse = await fetchLandDocuments({
            polygon_id: polygonId,
            records: records
        });

        if (Array.isArray(landResponse?.data?.response) && landResponse.data?.response.length > 0) {
            landResponse.data.response.forEach((docContent, index) => {
                const doc = createDocument(
                    `land_doc_${index}`,
                    `Land Document ${index + 1}`,
                    'Land',
                    'land',
                    getFileExtensionType(`document_${index}.jpg`),
                    docContent
                );
                if (doc) landDocs.push(doc);
            });
        }
    } catch (error) {
        console.error("Failed to fetch Land documents:", error);
    }
    return landDocs;
};



// --- Consolidated Policy Document Fetcher (New Export) ---
// This function combines calls to the individual fetchers above
export const loadPolicyDocumentsData = async (
  policyDetailCookie,
  setIsLoadingPolicyDocuments,
  setPolicyDocuments
) => {
  if (policyDetailCookie?.policy_document) {
    setIsLoadingPolicyDocuments(true);

  const {
    policy_document: policyDocName,
    tran_id: transactionId,
    endorsement_number: endorsementNumber,
    polygon_id: polygonId,
    records,
    farmer_id
  } = policyDetailCookie;

  const policyDocId = policyDocName?.replace(".pdf", "");

  try {
    const results = await Promise.all([
      fetchPolicyDocument(policyDocId, policyDocName),
      fetchCustomerDocument(policyDocId, policyDocName),
      fetchProposalDocument(transactionId, policyDocName),
      fetchEndorsementDocuments(endorsementNumber),
      fetchKYCDocuments({ farmer_id }),
      fetchLandDocumentsData(polygonId, records)
    ]);

    const fetchedDocs = results.flat().filter(Boolean);
    setPolicyDocuments(fetchedDocs);
  } catch (error) {
    console.error("Error fetching policy documents:", error);
    setPolicyDocuments([]);
  } finally {
    setIsLoadingPolicyDocuments(false);
  }
 };
}
